/**
 * SimAccidentsFilter — панель слоя «ДТП СИМ» (DA-15040).
 * - Радио периода: за всё время / 2025 / 2026 / за конкретную дату (последнее включает таймлайн).
 * - Переключатель категоризации (раскраска точек): тип инцидента / оператор / нарушитель.
 * - Тоглы-фильтры (комбинируются по И): есть погибшие / есть раненые / дети на СИМ.
 * - Легенда активной категоризации (свотчи цветов категорий).
 * Состояние — в mapFilters (плоское), смена — через дженерик onChangeMapFilters. Применение
 * фильтра/цвета на карте — Map.applySimAccidentsStyle (клиентский setFilter + setPaintProperty).
 */
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import { i18n } from '@lingui/core';
import { defineMessage, t } from '@lingui/macro';
import { SIM_ACCIDENTS_STYLE } from '../../../constants/layerStyles';
import { SIM_ACCIDENTS_SIZE_LEGEND } from '../../../constants/legends';
import { onChangeMapFilters } from '../../../ac/onChangeMapFilters';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import { StandardRadioGroup } from '../StandardRadioGroup';
import { LegendRenderer } from '../../LegendRenderer/LegendRenderer';
import styles from '../FilterSidebar.module.css';

// Ленивые defineMessage (не t``): массивы объявлены на уровне модуля, а t`` вычислился бы
// один раз в активной на момент импорта локали и не реагировал бы на смену языка. i18n._()
// в рендере переводит по текущей локали.
const PERIOD_OPTIONS = [
    { value: 'all',   label: defineMessage({ message: 'Сезоны 2025–2026 гг.' }) },
    { value: '2025',  label: defineMessage({ message: 'Сезон 2025 г.' }) },
    { value: '2026',  label: defineMessage({ message: 'Сезон 2026 г.' }) },
    { value: 'range', label: defineMessage({ message: 'Выбрать диапазон' }) },
    { value: 'date',  label: defineMessage({ message: 'Выбрать дату' }) },
];
const TOGGLES = [
    { key: 'simAccidentsKilled',   label: defineMessage({ message: 'Есть погибшие' }) },
    { key: 'simAccidentsInjured',  label: defineMessage({ message: 'Есть раненые' }) },
    { key: 'simAccidentsChildren', label: defineMessage({ message: 'Дети на СИМ' }) },
];
const SimAccidentsFilterComponent = ({
    simAccidentsPeriodMode, simAccidentsCategorizeBy,
    simAccidentsKilled, simAccidentsInjured, simAccidentsChildren,
    setFilter,
}) => {
    const toggles = { simAccidentsKilled, simAccidentsInjured, simAccidentsChildren };
    const cats = SIM_ACCIDENTS_STYLE.categories[simAccidentsCategorizeBy] || [];

    return (
        <div style={{ paddingLeft: 10, paddingRight: 10, marginTop: 10 }}>
            <div className={styles.legHead}>{t`Период:`}</div>
            <StandardRadioGroup
                value={simAccidentsPeriodMode}
                onChange={({ target: { value } }) => setFilter('simAccidentsPeriodMode', value)}
            >
                {PERIOD_OPTIONS.map(({ value, label }) => (
                    <FormControlLabel
                        key={value}
                        className={styles.isochroneFilterLabel}
                        control={
                            <Radio
                                checked={simAccidentsPeriodMode === value}
                                onChange={({ target }) => setFilter('simAccidentsPeriodMode', target.value)}
                                value={value}
                                className={classnames(styles.isochroneFilterInput, {
                                    [styles.checkedIsochroneFilterInput]: simAccidentsPeriodMode === value,
                                })}
                                size="small"
                            />
                        }
                        label={i18n._(label)}
                        labelPlacement="end"
                    />
                ))}
            </StandardRadioGroup>

            <div className={styles.legHead}>{t`Категоризация:`}</div>
            <StandardRadioGroup
                value={simAccidentsCategorizeBy}
                onChange={({ target: { value } }) => setFilter('simAccidentsCategorizeBy', value)}
            >
                {SIM_ACCIDENTS_STYLE.categorizations.map(({ key, label }) => (
                    <FormControlLabel
                        key={key}
                        className={styles.isochroneFilterLabel}
                        control={
                            <Radio
                                checked={simAccidentsCategorizeBy === key}
                                onChange={({ target }) => setFilter('simAccidentsCategorizeBy', target.value)}
                                value={key}
                                className={classnames(styles.isochroneFilterInput, {
                                    [styles.checkedIsochroneFilterInput]: simAccidentsCategorizeBy === key,
                                })}
                                size="small"
                            />
                        }
                        label={i18n._(label)}
                        labelPlacement="end"
                    />
                ))}
            </StandardRadioGroup>

            <div className={styles.legHead}>{t`Фильтры:`}</div>
            <div className={styles.checkboxIconGrid}>
                {TOGGLES.map(({ key, label }) => (
                    <Fragment key={key}>
                        <span />
                        <label htmlFor={key}>{i18n._(label)}</label>
                        <div>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    name="sim-accidents-toggle"
                                    id={key}
                                    checked={!!toggles[key]}
                                    onChange={e => setFilter(key, e.target.checked)}
                                />
                                <span className={classnames(styles.sliderRound, styles.slider)} />
                            </label>
                        </div>
                    </Fragment>
                ))}
            </div>

            <div className={styles.legHead}>{t`Легенда:`}</div>
            <div className={classnames(styles.checkboxIconGrid, styles.simAccidentsLegendGrid)}>
                {cats.map(({ value, color, label }) => (
                    <Fragment key={value}>
                        <span style={{ justifySelf: 'center', display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: color, border: '1px solid rgba(0,0,0,0.35)' }} />
                        <label>{i18n._(label)}</label>
                        <div />
                    </Fragment>
                ))}
                {simAccidentsCategorizeBy === 'offender' && (
                    <Fragment key="__fallback">
                        <span style={{ justifySelf: 'center', display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: SIM_ACCIDENTS_STYLE.fallbackColor, border: '1px solid rgba(0,0,0,0.35)' }} />
                        <label>{i18n._(SIM_ACCIDENTS_STYLE.fallbackLabel)} (2025)</label>
                        <div />
                    </Fragment>
                )}
            </div>

            <LegendRenderer config={SIM_ACCIDENTS_SIZE_LEGEND} />
            <div style={{ marginBottom: 16 }} />
        </div>
    );
};
const mapStateToProps = ({ mapFilters: {
    simAccidentsPeriodMode, simAccidentsCategorizeBy,
    simAccidentsKilled, simAccidentsInjured, simAccidentsChildren,
} }) => ({
    simAccidentsPeriodMode, simAccidentsCategorizeBy,
    simAccidentsKilled, simAccidentsInjured, simAccidentsChildren,
});
const mapDispatchToProps = dispatch => ({
    setFilter: (key, value) => dispatch(onChangeMapFilters(key, value)),
});
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(SimAccidentsFilterComponent);

export const SimAccidentsFilter = withDisplayedLayersCheck(
    ConnectedComponent,
    t`ДТП СИМ`
);
